import React, { PropsWithChildren, useState } from 'react'
import {
  EnrollmentInviteStatusEnum,
  EnrollmentStatus,
  FetchEnrollmentInvitesStatusEnum,
} from '../../swagger'
import { enrollments as enrollmentsApi } from '../../api/swagger'
import { defaultPageSize } from '../Pagination/TableFooterPagination'
import EnrollmentInviteRowInfo from '../Interfaces/EnrollmentInviteRowInfo'
import EnrollmentRowInfo from '../Interfaces/EnrollmentRowInfo'

export const ENROLLMENT_INVITES_MIN_SEARCH_LENGTH = 2
export const ENROLLMENT_MIN_SEARCH_LENGTH = 3

export enum ApplicationTypeFilterOptions {
  AllTypes = 'All Application Types',
  New = 'New',
  Returning = 'Returning',
  Invitation = 'Invitation',
}

export interface EnrollmentFilters {
  applicationType: ApplicationTypeFilterOptions
  enrollmentStatus: string
  tutorId: string | number
}

interface EnrollmentInviteFilters {
  inviteStatus: string
}

export const enrollmentStatusFilterOptions = {
  AllEnrollmentStatuses: 'All Enrollment Statuses',
  ...EnrollmentStatus,
}

export const enrollmentInviteStatusFilterOptions = {
  AllStatus: 'All Invite Statuses',
  ...EnrollmentInviteStatusEnum,
}

export const defaultTutorFilterOption = 'All Tutors'

export const defaultProgramDetailsContextValue = {
  /** Available filters for the enrollments. */
  enrollmentFilters: {
    applicationType: ApplicationTypeFilterOptions.AllTypes,
    enrollmentStatus: enrollmentStatusFilterOptions.AllEnrollmentStatuses,
    tutorId: defaultTutorFilterOption as EnrollmentFilters['tutorId'],
  },

  /** Available filters for the enrollment invites. */
  enrollmentInviteFilters: {
    inviteStatus: enrollmentInviteStatusFilterOptions.AllStatus,
  },

  /** Indicates if the user has enrollment invites with in progress status. */
  hasEnrollmentInvitesWithInProgressStatus: false,

  /** Indicates if the user is searching or filtering the enrollment invites. */
  isSearchingOrFilteringEnrollmentInvites: false,

  /** Search value for the enrollments. */
  searchEnrollments: '',

  /** Search value for the enrollment invites. */
  searchEnrollmentInvites: '',

  /** Pagination info for the enrollments. */
  enrollmentsPagination: {
    totalCount: 0,
    pageSize: defaultPageSize,
    page: 1,
  },

  /** Pagination info for the enrollment invites. */
  enrollmentInvitesPagination: {
    totalCount: 0,
    pageSize: defaultPageSize,
    page: 1,
  },

  /** Enrollments for a program. */
  enrollments: [] as EnrollmentRowInfo[],

  /** Enrollments invites for a program. */
  enrollmentInvites: [] as EnrollmentInviteRowInfo[],

  fetchEnrollments: async (programKey: number): Promise<void> => {
    console.warn(
      `The ProgramDetailsContext.fetchEnrollments was called with ${programKey}. Did you forget to use a ProgramDetailsProvider?`
    )
  },

  fetchEnrollmentInvites: async (programKey: number): Promise<void> => {
    console.warn(
      `The ProgramDetailsContext.fetchEnrollmentInvites was called with ${programKey}. Did you forget to use a ProgramDetailsProvider?`
    )
  },

  handleEnrollmentsPageChange: (page: number): void => {
    console.warn(
      `The ProgramDetailsContext.handleEnrollmentsPageChange was called with ${page} value. Did you forget to use a ProgramDetailsProvider?`
    )
  },

  handleEnrollmentInvitesPageChange: (page: number): void => {
    console.warn(
      `The ProgramDetailsContext.handleEnrollmentInvitesPageChange was called with ${page} value. Did you forget to use a ProgramDetailsProvider?`
    )
  },

  handleEnrollmentsPageSizeChange: (pageSize: number): void => {
    console.warn(
      `The ProgramDetailsContext.handleEnrollmentsPageSizeChange was called with ${pageSize} value. Did you forget to use a ProgramDetailsProvider?`
    )
  },

  handleEnrollmentInvitesPageSizeChange: (pageSize: number): void => {
    console.warn(
      `The ProgramDetailsContext.handleEnrollmentInvitesPageSizeChange was called with ${pageSize} value. Did you forget to use a ProgramDetailsProvider?`
    )
  },

  updateEnrollmentFilters: (filters: EnrollmentFilters): void => {
    console.warn(
      `The ProgramDetailsContext.updateEnrollmentFilters was called with ${filters} value. Did you forget to use a ProgramDetailsProvider?`
    )
  },

  updateEnrollmentInviteFilters: (filters: EnrollmentInviteFilters): void => {
    console.warn(
      `The ProgramDetailsContext.updateEnrollmentInviteFilters was called with ${filters} value. Did you forget to use a ProgramDetailsProvider?`
    )
  },

  updateSearchEnrollments: (search: string): void => {
    console.warn(
      `The ProgramDetailsContext.updateSearchEnrollments was called with ${search} value. Did you forget to use a ProgramDetailsProvider?`
    )
  },

  updateSearchEnrollmentInvites: (search: string): void => {
    console.warn(
      `The ProgramDetailsContext.updateSearchEnrollmentInvites was called with ${search} value. Did you forget to use a ProgramDetailsProvider?`
    )
  },
}

const ProgramDetailsContext = React.createContext(
  defaultProgramDetailsContextValue
)

export const useProgramDetailsContext =
  (): typeof defaultProgramDetailsContextValue =>
    React.useContext(ProgramDetailsContext)

const ProgramDetailsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [searchEnrollments, setSearchEnrollments] = useState('')
  const [searchEnrollmentInvites, setSearchEnrollmentInvites] = useState('')

  const [enrollments, setEnrollments] = useState<EnrollmentRowInfo[]>([])

  const [enrollmentInvites, setEnrollmentInvites] = useState<
    EnrollmentInviteRowInfo[]
  >([])

  const [enrollmentsPagination, setEnrollmentsPagination] = useState({
    totalCount: 0,
    pageSize: defaultPageSize,
    page: 1,
  })

  const [enrollmentInvitesPagination, setEnrollmentInvitesPagination] =
    useState({
      totalCount: 0,
      pageSize: defaultPageSize,
      page: 1,
    })

  const [enrollmentFilters, setEnrollmentFilters] = useState<EnrollmentFilters>(
    {
      applicationType: ApplicationTypeFilterOptions.AllTypes,
      enrollmentStatus: enrollmentStatusFilterOptions.AllEnrollmentStatuses,
      tutorId: defaultTutorFilterOption,
    }
  )

  const [enrollmentInviteFilters, setEnrollmentInviteFilters] =
    useState<EnrollmentInviteFilters>({
      inviteStatus: enrollmentInviteStatusFilterOptions.AllStatus,
    })

  const fetchEnrollments = async (programKey: number) => {
    const tutorActorKey =
      enrollmentFilters.tutorId !== defaultTutorFilterOption
        ? Number(enrollmentFilters.tutorId)
        : undefined

    const enrollmentStatus = (
      enrollmentFilters.enrollmentStatus !==
      enrollmentStatusFilterOptions.AllEnrollmentStatuses
        ? enrollmentFilters.enrollmentStatus
        : undefined
    ) as EnrollmentStatus

    const search =
      searchEnrollments.trim().length >= ENROLLMENT_MIN_SEARCH_LENGTH
        ? searchEnrollments
        : undefined

    const fetchedEnrollments = await enrollmentsApi.fetchEnrollments({
      programKey,
      tutorActorKey,
      enrollmentStatus,
      search,
      page: enrollmentsPagination.page,
      pageSize: enrollmentsPagination.pageSize,
      year: undefined,
      /**
       * Since the sortEnrollmentsByStudentName helper was removed
       * from the EnrollmentsTable component and the order of the enrollments
       * is now determined by the backend, we need to pass the sortBy property
       * to the endpoint to get the same order as before.
       */
      orderBy: ['familyName ASC', 'studentName ASC'],
    })

    const enrollments = fetchedEnrollments.enrollments.map((enrollment) => {
      return {
        rowId: `${enrollment.studentKey}${enrollment.programKey}`,
        ...enrollment,
      }
    })

    setEnrollments(enrollments)

    if (fetchedEnrollments.pagination) {
      const { page, pageSize, totalCount } = fetchedEnrollments.pagination
      setEnrollmentsPagination({
        page,
        pageSize,
        totalCount,
      })
    }
  }

  const fetchEnrollmentInvites = async (programKey: number) => {
    const search =
      searchEnrollmentInvites.trim().length >= 2
        ? searchEnrollmentInvites
        : undefined

    const status =
      enrollmentInviteFilters.inviteStatus !==
      enrollmentInviteStatusFilterOptions.AllStatus
        ? (enrollmentInviteFilters.inviteStatus as FetchEnrollmentInvitesStatusEnum)
        : undefined

    const fetchedEnrollmentInvites =
      await enrollmentsApi.fetchEnrollmentInvites({
        programKey,
        page: enrollmentInvitesPagination.page,
        pageSize: enrollmentInvitesPagination.pageSize,
        search,
        status,
        orderBy: ['parentLastName ASC'],
      })

    if (fetchedEnrollmentInvites.enrollmentInvites) {
      const enrollmentInvites = fetchedEnrollmentInvites.enrollmentInvites.map(
        (enrollmentInvite) => {
          const program = enrollmentInvite.programs.find(
            (program) => program.programKey === programKey
          )
          return {
            rowId: enrollmentInvite.uuid,
            familyName: enrollmentInvite.parentLastName,
            studentKey: -1,
            inviteDate: enrollmentInvite.invitationDate,
            enrollmentInviteStatus: enrollmentInvite.status,
            sentToEmail: enrollmentInvite.sentToEmail,
            offeredSpots: program?.offeredSpots ?? 0,
            skippedSpots: program?.skippedSpots ?? 0,
            primaryContactId: enrollmentInvite.parentUserKey,
          }
        }
      )
      setEnrollmentInvites(enrollmentInvites)
    }

    if (fetchedEnrollmentInvites.pagination) {
      const { page, pageSize, totalCount } = fetchedEnrollmentInvites.pagination
      setEnrollmentInvitesPagination({
        page,
        pageSize,
        totalCount,
      })
    }
  }

  const handleEnrollmentsPageChange = (page: number) => {
    setEnrollmentsPagination({
      ...enrollmentsPagination,
      page: page + 1,
    })
  }

  const handleEnrollmentInvitesPageChange = (page: number) => {
    setEnrollmentInvitesPagination({
      ...enrollmentInvitesPagination,
      page: page + 1,
    })
  }

  const handleEnrollmentsPageSizeChange = (pageSize: number) => {
    setEnrollmentsPagination({ ...enrollmentsPagination, pageSize })
  }

  const handleEnrollmentInvitesPageSizeChange = (pageSize: number) => {
    setEnrollmentInvitesPagination({ ...enrollmentInvitesPagination, pageSize })
  }

  const isSearchingEnrollmentInvites = (): boolean => {
    return (
      searchEnrollmentInvites.length >= ENROLLMENT_INVITES_MIN_SEARCH_LENGTH
    )
  }

  const isFilteringEnrollmentInvites = (): boolean => {
    return (
      enrollmentInviteFilters.inviteStatus !==
      enrollmentInviteStatusFilterOptions.AllStatus
    )
  }

  const isSearchingOrFilteringEnrollmentInvites =
    isSearchingEnrollmentInvites() || isFilteringEnrollmentInvites()

  const hasEnrollmentInvitesWithInProgressStatus =
    enrollmentInvites &&
    enrollmentInvites.some(
      (enrollmentInvite) =>
        enrollmentInvite.enrollmentInviteStatus ===
        EnrollmentInviteStatusEnum.InProgress
    )

  const value = {
    enrollmentFilters,
    enrollmentInviteFilters,
    enrollmentInvites,
    enrollmentInvitesPagination,
    enrollments,
    enrollmentsPagination,
    hasEnrollmentInvitesWithInProgressStatus,
    isSearchingOrFilteringEnrollmentInvites,
    searchEnrollmentInvites,
    searchEnrollments,

    /** functions */
    fetchEnrollmentInvites,
    fetchEnrollments,
    handleEnrollmentInvitesPageChange,
    handleEnrollmentInvitesPageSizeChange,
    handleEnrollmentsPageChange,
    handleEnrollmentsPageSizeChange,
    updateEnrollmentFilters: setEnrollmentFilters,
    updateEnrollmentInviteFilters: setEnrollmentInviteFilters,
    updateSearchEnrollmentInvites: setSearchEnrollmentInvites,
    updateSearchEnrollments: setSearchEnrollments,
  }

  return (
    <ProgramDetailsContext.Provider value={value}>
      {children}
    </ProgramDetailsContext.Provider>
  )
}

export default ProgramDetailsProvider
